import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, CheckCircle2, Lock, RotateCcw } from "lucide-react";
import React from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";

import {
  approveProject,
  getProject,
  reopenProject,
  type PdevMeta,
} from "@/api/product-dev";
import { Button } from "@/components/ui/button";
import { BomTreePanel } from "@/pages/product-dev/BomTreePanel";
import { AttachmentsPanel, NotesPanel, TechCardPanel } from "@/pages/product-dev/panels";
import { STAGE_STYLES, apiErr, money } from "@/pages/product-dev/format";

interface Props {
  projectId: string;
  meta: PdevMeta;
  onBack: () => void;
}

type Tab = "bom" | "attachments" | "notes" | "techcard";

const TABS: { key: Tab; label: string }[] = [
  { key: "bom", label: "Состав продукта" },
  { key: "attachments", label: "Фото и макеты" },
  { key: "notes", label: "Заметки" },
  { key: "techcard", label: "Техкарта" },
];

export function ProjectDetail({ projectId, meta, onBack }: Props): React.ReactElement {
  const qc = useQueryClient();
  // Активная вкладка — в URL (`?tab=`), чтобы ссылка/скриншот-харнесс открывали
  // нужную вкладку напрямую.
  const [searchParams, setSearchParams] = useSearchParams();
  const urlTab = searchParams.get("tab");
  const tab: Tab = TABS.some((t) => t.key === urlTab) ? (urlTab as Tab) : "bom";
  const setTab = (t: Tab): void =>
    setSearchParams((prev) => {
      prev.set("tab", t);
      return prev;
    });
  const project = useQuery({
    queryKey: ["pdev", "project", projectId],
    queryFn: () => getProject(projectId),
  });

  const invalidateAll = (): void => {
    qc.invalidateQueries({ queryKey: ["pdev", "project", projectId] });
    qc.invalidateQueries({ queryKey: ["pdev", "projects"] });
    qc.invalidateQueries({ queryKey: ["pdev", "outputs", projectId] });
    qc.invalidateQueries({ queryKey: ["pdev", "tree", projectId] });
    qc.invalidateQueries({ queryKey: ["pdev", "rollup", projectId] });
  };

  const approve = useMutation({
    mutationFn: () => approveProject(projectId),
    onSuccess: (snaps) => {
      invalidateAll();
      const totals = snaps
        .map((s) => `${s.output_title}: ${money(s.total_cost, s.currency)}`)
        .join("; ");
      toast.success(`Утверждён. Себестоимость — ${totals || "рассчитана"}`);
    },
    onError: (e) => toast.error(apiErr(e, "Не удалось утвердить")),
  });

  const reopen = useMutation({
    mutationFn: () => reopenProject(projectId),
    onSuccess: () => {
      invalidateAll();
      toast.success("Возвращён в разработку");
    },
    onError: (e) => toast.error(apiErr(e, "Не удалось вернуть в разработку")),
  });

  const p = project.data;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <Button variant="ghost" size="icon" onClick={onBack} aria-label="Назад">
          <ArrowLeft className="size-5" />
        </Button>
        <h1 className="text-2xl font-semibold tracking-tight">{p?.name ?? "…"}</h1>
        {p && (
          <span
            className={`rounded-full px-2.5 py-1 text-xs font-medium ${STAGE_STYLES[p.stage] ?? ""}`}
          >
            {meta.stages[p.stage]}
          </span>
        )}
        {p?.is_locked && (
          <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
            <Lock className="size-3.5" /> залочен
          </span>
        )}
        <div className="ml-auto flex items-center gap-2">
          {p && !p.is_locked && (
            <Button
              size="sm"
              onClick={() => approve.mutate()}
              disabled={approve.isPending}
            >
              <CheckCircle2 className="mr-1 size-4" /> Утвердить
            </Button>
          )}
          {p?.is_locked && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => reopen.mutate()}
              disabled={reopen.isPending}
            >
              <RotateCcw className="mr-1 size-4" /> В разработку
            </Button>
          )}
        </div>
      </div>

      <div className="flex flex-wrap gap-1 border-b">
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            onClick={() => setTab(t.key)}
            className={`-mb-px border-b-2 px-3 py-2 text-sm transition-colors ${
              tab === t.key
                ? "border-primary font-medium text-foreground"
                : "border-transparent text-muted-foreground hover:text-foreground"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "bom" && <BomTreePanel projectId={projectId} meta={meta} locked={!!p?.is_locked} />}
      {tab === "attachments" && <AttachmentsPanel projectId={projectId} meta={meta} />}
      {tab === "notes" && <NotesPanel projectId={projectId} />}
      {tab === "techcard" && <TechCardPanel projectId={projectId} meta={meta} />}
    </div>
  );
}
